import { useEffect, useState } from "react";
import api from "@/lib/api";
import StatusBadge from "@/components/common/StatusBadge";
import { Loader2 } from "lucide-react";

export default function WalletLedger() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ txn_type: "", player_id: "", operator_id: "" });
  const [busy, setBusy] = useState(false);
  const [round, setRound] = useState(null);

  const load = async () => {
    setBusy(true);
    const params = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => v && params.set(k, v));
    params.set("limit", "200");
    const { data } = await api.get(`/api/v1/wallet/transactions?${params.toString()}`);
    setItems(data); setBusy(false);
  };
  useEffect(() => { load(); }, [filter]);

  const openRound = async (round_id) => {
    if (!round_id) return;
    const { data } = await api.get(`/api/v1/wallet/rounds/${round_id}`);
    setRound(data);
  };

  return (
    <div className="space-y-6" data-testid="wallet-ledger-page">
      <div className="text-[var(--text-2)] text-sm">Append-only ledger. Every wallet operation is here, idempotent and immutable.</div>

      <div className="flex flex-wrap gap-3">
        <select value={filter.txn_type} onChange={(e) => setFilter({ ...filter, txn_type: e.target.value })}
                className="px-3 py-2 bg-[var(--surface)] border border-[var(--border)] rounded text-sm" data-testid="ledger-type-filter">
          <option value="">All types</option>
          {["BET", "WIN", "ROLLBACK", "REFUND", "BONUS", "ADJUSTMENT", "DEPOSIT", "WITHDRAWAL"].map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input placeholder="Player ID" value={filter.player_id} onChange={(e) => setFilter({ ...filter, player_id: e.target.value })}
               className="px-3 py-2 bg-[var(--surface)] border border-[var(--border)] rounded text-sm font-mono" data-testid="ledger-player-filter"/>
        <input placeholder="Operator ID" value={filter.operator_id} onChange={(e) => setFilter({ ...filter, operator_id: e.target.value })}
               className="px-3 py-2 bg-[var(--surface)] border border-[var(--border)] rounded text-sm font-mono" data-testid="ledger-operator-filter"/>
      </div>

      <div className="card-surface overflow-hidden">
        <div className="overflow-x-auto max-h-[70vh]">
          <table className="aggr-table">
            <thead><tr><th>Txn ID</th><th>Type</th><th>Amount</th><th>Balance Before → After</th><th>Player</th><th>Round</th><th>Status</th><th>When</th></tr></thead>
            <tbody>
              {busy && <tr><td colSpan={8} className="text-center py-6"><Loader2 className="inline animate-spin" size={14}/></td></tr>}
              {!busy && items.map(t => (
                <tr key={t.id}>
                  <td className="font-mono text-xs text-[var(--text-2)]">{t.id}</td>
                  <td><StatusBadge status={t.txn_type}/></td>
                  <td className="font-mono">{t.amount.toFixed(2)} <span className="text-[var(--text-3)] text-xs">{t.currency}</span></td>
                  <td className="font-mono text-xs">
                    <span className="text-[var(--text-3)]">{t.balance_before.toFixed(2)}</span>
                    <span className="text-[var(--gold)] mx-1">→</span>
                    <span>{t.balance_after.toFixed(2)}</span>
                  </td>
                  <td className="font-mono text-xs">{t.player_id}</td>
                  <td>
                    {t.round_id ? (
                      <button onClick={() => openRound(t.round_id)} className="font-mono text-xs text-[var(--gold)] hover:underline" data-testid={`open-round-${t.round_id}`}>
                        {t.round_id.slice(0, 14)}…
                      </button>
                    ) : <span className="text-[var(--text-3)]">—</span>}
                  </td>
                  <td><StatusBadge status={t.status}/></td>
                  <td className="text-xs text-[var(--text-3)]">{new Date(t.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {round && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setRound(null)}>
          <div className="card-surface w-full max-w-2xl p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="round-detail">
            <div className="text-xs uppercase tracking-widest text-[var(--text-3)]">Round</div>
            <div className="text-xl font-semibold font-mono mt-1">{round.round.round_id}</div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div><div className="text-[10px] uppercase tracking-widest text-[var(--text-3)]">Total bet</div><div className="font-mono text-lg">{round.round.total_bet}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-[var(--text-3)]">Total win</div><div className="font-mono text-lg">{round.round.total_win}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-[var(--text-3)]">Status</div><div className="mt-1"><StatusBadge status={round.round.status}/></div></div>
            </div>
            <div className="text-sm font-semibold mt-6 mb-2">Transactions</div>
            <table className="aggr-table">
              <thead><tr><th>Type</th><th>Amount</th><th>Balance after</th></tr></thead>
              <tbody>
                {round.transactions.map(t => (
                  <tr key={t.id}><td><StatusBadge status={t.txn_type}/></td><td className="font-mono">{t.amount.toFixed(2)}</td><td className="font-mono">{t.balance_after.toFixed(2)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
